import React, { useState, useEffect } from 'react';
import { X, FileText, Save, AlertCircle, ExternalLink } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { landingService } from '../services/landingService';
import { useAuth } from '../context/AuthContext';

const LandingConfigForm = ({ isOpen, onClose, clubId, clubSlug }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [formData, setFormData] = useState({
    step1_content: '',
    slug: '',
    active: true
  });
  const [errors, setErrors] = useState({});

  // Configuración del editor Quill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'align',
    'link', 'image'
  ];

  useEffect(() => {
    if (isOpen && clubId) {
      loadConfig();
    }
  }, [isOpen, clubId]);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const config = await landingService.getConfig(clubId);
      setFormData({
        step1_content: config.step1_content || '',
        slug: clubSlug || '',
        active: config.active ?? true
      });
    } catch (error) {
      console.error('Error cargando configuración:', error);
      // Si no existe configuración, usar valores por defecto
      setFormData({
        step1_content: `<h2>Bienvenido a nuestro club</h2><p>Únete a nosotros y disfruta de todas nuestras actividades.</p>`,
        slug: clubSlug || '',
        active: true
      });
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      step1_content: content
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'El slug solo puede contener letras minúsculas, números y guiones';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Actualizar configuración
      await landingService.updateConfig(clubId, {
        step1_content: formData.step1_content,
        active: formData.active
      });

      // Actualizar slug si cambió
      if (formData.slug && formData.slug !== clubSlug) {
        await landingService.updateSlug(clubId, formData.slug);
      }

      onClose();
      // Recargar la página o actualizar datos
      window.location.reload();
    } catch (error) {
      console.error('Error guardando configuración:', error);
      const errorMessage = error.response?.data?.message || 'Error al guardar la configuración. Inténtalo de nuevo.';
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const getLandingUrl = () => {
    const slug = formData.slug || clubSlug;
    if (!slug) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/landing/${slug}`;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <FileText size={24} className="text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Configurar Landing de Inscripción
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-3" />
              <span className="text-red-800">{errors.submit}</span>
            </div>
          )}

          {loadingConfig ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando configuración...</p>
            </div>
          ) : (
            <>
              {/* URL de la landing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de la Landing
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={getLandingUrl()}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                  {formData.slug || clubSlug ? (
                    <a
                      href={getLandingUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      Ver Landing
                    </a>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Comparte esta URL para que las personas se inscriban en tu club
                </p>
              </div>

              {/* Slug */}
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                  Slug (URL personalizada)
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="mi-club"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.slug ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.slug && (
                  <p className="mt-1 text-sm text-red-600">{errors.slug}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Solo letras minúsculas, números y guiones. Ejemplo: mi-club-de-montana
                </p>
              </div>

              {/* Contenido del Paso 1 */}
              <div className="border-t border-gray-200 pt-6">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  Contenido del Paso 1 (Texto explicativo)
                </label>
                
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formData.step1_content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Escribe aquí el contenido que verán los usuarios en el primer paso de la landing..."
                    style={{ minHeight: '300px' }}
                  />
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 mb-2">💡 Ayuda: Usa la barra de herramientas para formatear el texto</p>
                  <div className="text-xs text-blue-800 space-y-1">
                    <div>• <strong>Negrita</strong>, <em>Cursiva</em>, <u>Subrayado</u> - Para resaltar texto</div>
                    <div>• Títulos (H1, H2, H3) - Para encabezados</div>
                    <div>• Colores - Para cambiar el color del texto y fondo</div>
                    <div>• Listas - Para crear listas ordenadas o con viñetas</div>
                    <div>• Alineación - Para alinear el texto (izquierda, centro, derecha)</div>
                    <div>• Enlaces e imágenes - Para agregar contenido multimedia</div>
                  </div>
                </div>
              </div>

              {/* Estado activo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                  Landing activa
                </label>
              </div>
            </>
          )}

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || loadingConfig}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Guardar Configuración
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LandingConfigForm;
